import React from "react";
import { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import Header from "../components/HomePage/header";
import OrderSummary from "./OrderSummary";
import { HashLoader } from "react-spinners"; // Importing the spinner
import {
  CreditCard,
  Check,
  LockKeyhole,
  Mail,
  MapPin,
  ShieldCheck,
  User,
} from "lucide-react";
import { COUNTRY_OPTIONS } from "./countries";

export default function CheckoutForm({ subscriptionData }) {
  const [loading, setLoading] = useState(false); // To control loading state

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Inter", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
        // Add the border styles here
        border: "1px solid #ced4da",
        borderRadius: "8px",
        padding: "10px",
        backgroundColor: "#fff",
        boxShadow: "none",
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
        // Add the invalid border styles here
        border: "1px solid #fa755a",
      },
    },
    hidePostalCode: true,
  };

  //  Calculation of Price
  const base_Price = subscriptionData?.price; // Base price of the subscription plan
  const plan_Duration = subscriptionData.duration; // Duration of the subscription plan
  const base_gst = 18; // GST rate in percentage

  // Calculate the total price for the given duration
  const total_Price_Before_Gst = base_Price * plan_Duration;

  // Calculate the GST amount
  const plan_Gst = (total_Price_Before_Gst * base_gst) / 100;

  // Calculate the final total price including GST
  const total_Price = total_Price_Before_Gst + plan_Gst;

  //Stipe Integer Pricing

  const strip_Price = total_Price * 100;

  console.log("Total Price Including Gst:", total_Price);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [step, setStep] = useState(0); // Track the current step (0 - 4)
  const [isProcessing, setIsProcessing] = useState(false); // Track if processing has started
  const [billingStreetAddress, setBillingStreetAddress] = useState("");
  const [billingCountry, setBillingCountry] = useState("");
  const [billingZip, setBillingZip] = useState("");

  const handleCheckout = async (event) => {
    event.preventDefault();

    setLoading(true);
    setErrorMessage("");
    setIsProcessing(true);
    setStep(0);

    if (!stripe || !elements) {
      setErrorMessage("Stripe not loaded");
      setLoading(false);
      setIsProcessing(false);
      return;
    }

    const billingDetails = {
      streetAddress: billingStreetAddress.trim(),
      country: billingCountry,
      zip: billingZip.trim(),
    };

    if (
      !billingDetails.streetAddress ||
      !billingDetails.country ||
      !billingDetails.zip
    ) {
      setErrorMessage("Billing address, country, and ZIP are required.");
      setLoading(false);
      setIsProcessing(false);
      return;
    }

    try {
      /* =========================
         1️⃣ REGISTER USER
      ========================= */
      setStep(1);

      const registerRes = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, firstname, lastname }),
      });

      // 409 = already exists → allowed
      if (!registerRes.ok && registerRes.status !== 409) {
        const err = await registerRes.json().catch(() => ({}));
        setErrorMessage(err.error || "Registration failed");
        return;
      }

      /* =========================
         2️⃣ CREATE STRIPE CUSTOMER
      ========================= */
      setStep(2);

      const customerRes = await fetch("/api/create-stripe-customer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });

      if (!customerRes.ok) {
        const err = await customerRes.json().catch(() => ({}));
        setErrorMessage(err.error || "Failed to create customer");
        return;
      }

      const { customerId } = await customerRes.json();

      /* =========================
         3️⃣ CREATE PAYMENT METHOD
      ========================= */
      setStep(3);

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setErrorMessage("Card input not found");
        return;
      }

      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            email: username,
            name: `${firstname} ${lastname}`,
            address: {
              line1: billingDetails.streetAddress,
              country: billingDetails.country,
              postal_code: billingDetails.zip,
            },
          },
        });

      if (pmError) {
        setErrorMessage(pmError.message);
        return;
      }

      /* =========================
         4️⃣ CREATE SUBSCRIPTION
      ========================= */
      setStep(4);

      const subRes = await fetch("/api/subscribes", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          customerId,
          paymentMethodId: paymentMethod.id,
          planId: subscriptionData.planID,
          plan: subscriptionData.plan,
          duration: subscriptionData.duration,
          price: subscriptionData.price * 100,
          billingDetails,
        }),
      });

      // ❌ Stop only on HTTP error
      if (!subRes.ok) {
        const err = await subRes.json().catch(() => ({}));
        setErrorMessage(err.error || "Subscription failed");
        return;
      }

      // ✅ THIS WAS MISSING
      const data = await subRes.json();

      /* =========================
   5️⃣ HANDLE 3DS (if needed)
========================= */

      const { clientSecret } = data;
      setStep(5);

      // 🔥 ALWAYS confirm if clientSecret exists
      if (clientSecret) {
        const result = await stripe.confirmCardPayment(clientSecret);

        console.log("CONFIRM RESULT:", result);

        if (result.error) {
          setErrorMessage(result.error.message);
          return;
        }
      }
      /* =========================
   6️⃣ SUCCESS
========================= */
      setStep(6);
      navigate("/success");
      return;
    } catch (err) {
      console.error(err);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }
  };

  const steps = [
    "Starting",
    "Account",
    "Customer",
    "Card",
    "Subscription",
    "Verification",
    "Complete",
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 border-b border-gray-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-purple-600">
              Secure checkout
            </p>
            <h1 className="mt-2 text-3xl font-bold text-gray-950 sm:text-4xl">
              Complete your PrepNexis plan
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
              Create your account, add payment details, and start practicing
              with your selected PTE preparation plan.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Encrypted payment
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start">
          <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-5 sm:px-7">
              <p className="text-xl font-semibold text-gray-950">
                Payment details
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Use the same email you want linked to your PrepNexis account.
              </p>
            </div>

            <form onSubmit={handleCheckout} className="space-y-7 px-5 py-6 sm:px-7">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Account
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="email"
                        name="username"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="your.email@gmail.com"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="firstname"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      First name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="firstname"
                        name="firstname"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="First name"
                        value={firstname}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                      <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="lastname"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Last name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="lastname"
                        name="lastname"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="Last name"
                        value={lastname}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                      <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        id="password"
                        name="password"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="***********"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirm-password"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        id="confirm-password"
                        name="confirm-password"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="***********"
                      />
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Card
                </h2>
                <div className="mt-4 grid gap-4">
                  <div>
                    <label
                      htmlFor="card-holder"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Card holder
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="card-holder"
                        name="card-holder"
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm uppercase text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                        placeholder="Your full name here"
                      />
                      <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="card-no"
                      className="mb-2 block text-sm font-medium text-gray-800"
                    >
                      Card details
                    </label>
                    <div
                      className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm transition focus-within:border-purple-500 focus-within:ring-4 focus-within:ring-purple-100"
                      id="card-no"
                    >
                      <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Billing
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px_110px]">
                  <div className="relative">
                    <input
                      type="text"
                      id="billing-address"
                      name="billing-address"
                      className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 pl-11 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                      placeholder="Street address"
                      value={billingStreetAddress}
                      onChange={(e) => setBillingStreetAddress(e.target.value)}
                      required
                    />
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                  <select
                    type="text"
                    name="billing-country"
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                    value={billingCountry}
                    onChange={(e) => setBillingCountry(e.target.value)}
                    required
                  >
                    <option value="">Country</option>
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="billing-zip"
                    className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                    placeholder="ZIP"
                    value={billingZip}
                    onChange={(e) => setBillingZip(e.target.value)}
                    required
                  />
                </div>
              </div>

              {errorMessage && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {errorMessage}
                </div>
              )}

              {isProcessing && !errorMessage && (
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-950">
                        Securing your payment
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {steps[step]} in progress
                      </p>
                    </div>
                    <p className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700">
                      {Math.round((step / (steps.length - 1)) * 100)}%
                    </p>
                  </div>
                  <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-purple-600"
                      style={{
                        width: `${(step / (steps.length - 1)) * 100}%`,
                        transition: "width 0.5s ease-in-out",
                      }}
                      aria-valuenow={(step / (steps.length - 1)) * 100}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-7 gap-2">
                    {steps.map((item, index) => {
                      const isDone = step > index;
                      const isCurrent = step === index;

                      return (
                        <div
                          key={item}
                          className="flex min-w-0 flex-col items-center gap-1"
                        >
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold transition ${
                              isDone
                                ? "border-purple-600 bg-purple-600 text-white"
                                : isCurrent
                                ? "border-purple-600 bg-purple-50 text-purple-700 ring-4 ring-purple-100"
                                : "border-gray-200 bg-gray-50 text-gray-400"
                            }`}
                          >
                            {isDone ? (
                              <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              index + 1
                            )}
                          </div>
                          <span
                            className={`hidden max-w-full truncate text-[11px] font-medium sm:block ${
                              isCurrent ? "text-gray-900" : "text-gray-400"
                            }`}
                          >
                            {item}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className={`flex w-full items-center justify-center rounded-lg px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition duration-300 ease-in-out ${
                  loading
                    ? "cursor-not-allowed bg-gray-700 opacity-60"
                    : "bg-gray-900 hover:bg-gray-800"
                }`}
                disabled={loading} // Disable button during loading
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-3">
                    <HashLoader size={20} color="#fff" />
                    <span>Processing securely</span>
                  </div>
                ) : (
                  `Pay $${total_Price}`
                )}
              </button>
            </form>
          </section>

          <OrderSummary subscriptionData={subscriptionData} />
        </div>
      </main>
    </div>
  );
}
